<template>
    <div v-if="hasAnyPermission('duplicate-view')">
        <ToastPrime position="bottom-left" />
        <div class="py-4 container-fluid">
            <div class="card border-0" style="height: calc(100vh - 120px); display: flex; flex-direction: column;">

                <!-- HEADER -->
                <div class="card-header py-3 d-flex flex-wrap align-items-center gap-3">
                    <div class="d-flex flex-column">
                        <h5 class="mb-0">Check Duplicate Report</h5>
                        <small v-if="table.lastUpdated" class="text-xxs text-muted mt-1">Last generated: {{ table.lastUpdated }}</small>
                    </div>

                    <div class="d-flex flex-wrap gap-2 flex-grow-1 align-items-center">
                    </div>

                    <div class="d-flex gap-2 align-items-end">
                        <SelectPrime v-model="table.filter.type" :options="filterTypeOptions" option-label="label"
                            option-value="value" placeholder="Select Filter" class="filter-select" size="small"
                            @change="onFilterChange" />
                        <button type="button" class="mx-1 mb-0 btn btn-sm btn-info text-white" @click="confirmGenerate" :disabled="generating">
                            <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': generating }"></i> Generate Duplicate
                        </button>
                        <button v-if="canExportDuplicate" type="button" class="mx-1 mb-0 btn btn-sm btn-primary" @click="exportData" :disabled="table.onLoad">
                            <i class="pi pi-download me-1"></i> Export
                        </button>
                    </div>
                </div>

                <!-- TABLE -->
                <div ref="scrollContainer" style="flex: 1; overflow-y: auto;" @scroll="onScroll">
                    <DataTable v-model:expandedRows="expandedRows" :value="table.onLoad ? dummyRows : displayData"
                        data-key="id" removable-sort table-style="min-width: 100%; width: max-content;"
                        :row-class="getRowClass">

                        <Column expander style="width: 3rem" header-class="bg-light text-dark" />

                        <Column header="Material Information" header-class="bg-light text-dark" style="min-width: 30rem" sortable>
                            <template v-if="table.onLoad" #body>
                                <Skeleton />
                            </template>
                            <template v-else #body="slotProps">
                                <div class="d-flex flex-column">
                                    <div class="d-flex align-items-center mb-1">
                                        <span class="badge bg-primary-soft text-primary font-weight-bolder me-2 px-2"
                                            style="font-size: 11px; letter-spacing: 0.5px;">
                                            {{ slotProps.data.material_number }}
                                        </span>
                                        <h6 class="text-sm font-weight-bolder text-dark mb-0 text-truncate"
                                            style="max-width: 450px;" :title="slotProps.data.short_description">
                                            {{ slotProps.data.short_description ||
                                                extractShortText(slotProps.data.long_description) }}
                                        </h6>
                                    </div>
                                    <div class="text-xxs text-secondary mt-1 ps-1"
                                        style="font-family: 'Courier New', Courier, monospace; word-break: break-all; white-space: normal;">
                                        {{ slotProps.data.long_description }}
                                    </div>
                                </div>
                            </template>
                        </Column>

                        <Column header="Categorization" header-class="bg-light text-dark" style="min-width: 16rem">
                            <template v-if="table.onLoad" #body>
                                <Skeleton />
                            </template>
                            <template v-else #body="slotProps">
                                <div class="d-flex flex-column gap-1">
                                    <div class="d-flex align-items-center">
                                        <i class="fas fa-microchip text-xs text-info opacity-5 me-2"
                                            style="width: 14px"></i>
                                        <span class="text-xxs font-weight-bold text-dark">{{
                                            slotProps.data.material_type_description || slotProps.data.material_type || '-'
                                            }}</span>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <i class="fas fa-boxes text-xs text-info opacity-5 me-2" style="width: 14px"></i>
                                        <span class="text-xxs text-secondary">{{
                                            slotProps.data.material_group_description || slotProps.data.material_group ||
                                            '-'
                                            }}</span>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <i class="fas fa-tag text-xs text-info opacity-5 me-2" style="width: 14px"></i>
                                        <span class="text-xxs text-secondary">{{
                                            slotProps.data.uom_description || slotProps.data.uom || '-' }}</span>
                                    </div>
                                    <div v-if="slotProps.data.company_description" class="d-flex align-items-center">
                                        <i class="fas fa-building text-xs text-info opacity-5 me-2" style="width: 14px"></i>
                                        <span class="text-xxs text-secondary">{{ slotProps.data.company_description
                                            }}</span>
                                    </div>
                                </div>
                            </template>
                        </Column>

                        <Column header="Audit Data" header-class="bg-light text-dark" style="min-width: 12rem"
                            body-class="text-center">
                            <template v-if="table.onLoad" #body>
                                <Skeleton />
                            </template>
                            <template v-else #body="slotProps">
                                <div class="d-flex flex-column align-items-start">
                                    <span
                                        class="badge bg-danger-soft text-danger border border-danger-soft font-weight-bolder py-1 px-3"
                                        style="font-size: 10px; border-radius: 50px;">
                                        {{ slotProps.data.duplicate_count || (slotProps.data.duplicates ?
                                            slotProps.data.duplicates.length : 0) }} Potential Duplicates
                                    </span>
                                    <small class="text-xxs text-muted mt-1">Review matches below</small>
                                </div>
                            </template>
                        </Column>

                        <!-- ROW EXPANSION: Duplicate Items -->
                        <template #expansion="slotProps">
                            <div class="p-3 bg-light">
                                <div v-if="slotProps.data.duplicates && slotProps.data.duplicates.length > 0">
                                    <div v-for="(dup, index) in slotProps.data.duplicates" :key="'dup-' + index"
                                        class="duplicate-card-premium p-3 border-radius-lg border bg-white mb-2 shadow-sm">
                                        <!-- Header Row -->
                                        <div
                                            class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-light flex-wrap gap-2">
                                            <div class="d-flex align-items-center flex-wrap gap-2">
                                                <div v-if="dup.similarity !== undefined"
                                                    class="d-flex align-items-center me-2">
                                                    <div class="progress progress-xs w-60px me-2 mb-0" style="height: 4px;">
                                                        <div class="progress-bar" role="progressbar"
                                                            :style="{ width: (dup.similarity * 100) + '%', backgroundColor: getSimilarityHexColor(dup.similarity) }">
                                                        </div>
                                                    </div>
                                                    <span class="text-xs font-weight-bolder"
                                                        :style="{ color: getSimilarityHexColor(dup.similarity) }">
                                                        {{ (dup.similarity * 100).toFixed(0) }}%
                                                    </span>
                                                </div>
                                                <span v-else
                                                    class="badge bg-info-soft text-info text-xxs font-weight-bolder px-2 py-1 border border-info-soft">
                                                    PART NUMBER MATCH
                                                </span>
                                                <span class="badge bg-gray-100 text-dark text-xxs font-weight-bolder px-2">
                                                    M/N: {{ dup.material_number }}
                                                </span>
                                            </div>

                                            <div class="d-flex gap-3 text-xxs">
                                                <div class="d-flex flex-column align-items-end">
                                                    <span class="text-secondary font-weight-bolder opacity-5 text-uppercase"
                                                        style="font-size: 8px">Material Type</span>
                                                    <span class="text-dark font-weight-bold">{{
                                                        dup.material_type_description || dup.material_type || '-' }}</span>
                                                </div>
                                                <div class="d-flex flex-column align-items-end">
                                                    <span class="text-secondary font-weight-bolder opacity-5 text-uppercase"
                                                        style="font-size: 8px">Material Group</span>
                                                    <span class="text-dark font-weight-bold">{{
                                                        dup.material_group_description || dup.material_group || '-'
                                                        }}</span>
                                                </div>
                                                <div class="d-flex flex-column align-items-end border-start ps-3">
                                                    <span class="text-secondary font-weight-bolder opacity-5 text-uppercase"
                                                        style="font-size: 8px">UOM</span>
                                                    <span class="text-dark font-weight-bold">{{
                                                        dup.uom_description || dup.uom || '-' }}</span>
                                                </div>
                                                <div v-if="dup.company_description"
                                                    class="d-flex flex-column align-items-end border-start ps-3">
                                                    <span class="text-secondary font-weight-bolder opacity-5 text-uppercase"
                                                        style="font-size: 8px">Company</span>
                                                    <span class="text-dark font-weight-bold">{{
                                                        dup.company_description }}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Description -->
                                        <div class="duplicate-desc-wide bg-gray-50 border-radius-md p-3"
                                            style="overflow: hidden; width: 100%;">
                                            <p class="text-sm text-dark mb-0 lh-base"
                                                style="font-family: 'Courier New', Courier, monospace; width: 100%; font-size: 11px; word-break: break-all; white-space: normal; line-height: 22px;">
                                                {{ dup.long_description }}</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-center text-xxs text-muted py-3">
                                    No duplicates for this item.
                                </div>
                            </div>
                        </template>

                        <!-- EMPTY -->
                        <template #empty>
                            <div style="text-align: center; padding: 40px 0;">
                                <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7;" />
                                <div style="color:#888; font-size:1.1em; font-weight:500;">No Data</div>
                            </div>
                        </template>

                    </DataTable>

                    <!-- INFINITE SCROLL LOADER -->
                    <div v-if="table.loadingMore" class="text-center py-3 text-secondary">
                        <i class="fas fa-spinner fa-spin me-2"></i> Loading more...
                    </div>
                    <div v-else-if="!table.onLoad && table.data.length && table.page.current_pages >= table.page.total_pages"
                        class="text-center py-3 text-xxs text-muted">
                        All data loaded
                    </div>
                </div>

                <!-- FOOTER -->
                <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
                    <p class="mb-0">
                        {{ table.data.length }} of {{ table.total }}
                    </p>
                </div>

            </div>
        </div>

        <!-- EXPORT DIALOG -->
        <DialogPrime v-model:visible="exportModal.visible" modal style="width: 25rem; margin: 10px;">
            <template #header>
                <div class="d-flex align-items-center gap-3">
                    <h5 class="mb-0 fw-bold text-dark">Export Duplicate</h5>
                </div>
            </template>
            <div class="row g-4">
                <div class="col-12">
                    <label for="exportType" class="form-label fw-semibold">Select Type <span class="text-danger">*</span></label>
                    <SelectPrime
                        v-model="exportModal.selectedType"
                        :options="filterTypeOptions"
                        option-label="label"
                        option-value="value"
                        placeholder="Select type"
                        class="w-100"
                        size="small"
                    />
                </div>
            </div>
            <template #footer>
                <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
                    <ButtonPrime
                        type="button" label="Cancel" severity="danger" raised size="small"
                        class="px-4 border-0" @click="closeExportModal()" />
                    <ButtonPrime
                        type="button" severity="contrast" raised size="small"
                        :disabled="exportModal.onLoading" label="Export" class="px-4"
                        @click="confirmExport()">
                        <template v-if="exportModal.onLoading" #icon>
                            <i class="fas fa-spinner fa-spin me-2"></i>
                        </template>
                    </ButtonPrime>
                </div>
            </template>
        </DialogPrime>
    </div>
    <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import api from "@/helpers/api";
import checkDuplicateService from "@/services/report/checkDuplicateService";
import NoAccess from "@/components/NoAccess.vue";

export default {
    name: "CheckDuplicate",
    components: {
        NoAccess,
    },
    data() {
        return {
            table: {
                onLoad: true,
                loadingMore: false,
                search: this.$route.query.search || "",
                filter: {
                    type: this.$route.query.type || "duplicate_by_description",
                },
                total: 0,
                data: [],
                lastUpdated: null,
                limit: 100,
                page: {
                    current_pages: 1,
                    per_pages: 100,
                    total_pages: 1,
                },
            },
            expandedRows: {},
            filterTypeOptions: [
                { label: "Duplicate by Description", value: "duplicate_by_description" },
                { label: "Duplicate by Manufacture", value: "duplicate_by_manref" },
            ],
            dummyRows: Array.from({ length: 10 }, (_, i) => ({ id: `dummy-${i}` })),
            // hasil generate-duplicate (total per tipe)
            summary: {
                description_total: 0,
                manref_total: 0,
            },
            generating: false,
            exportModal: {
                visible: false,
                selectedType: '',
                onLoading: false,
            },
        };
    },

    computed: {
        ...mapState({
            authUserPermissions: (state) => state.auth.userHasPermissions || [],
        }),
        displayData() {
            return this.table.data;
        },
        canExportDuplicate() {
            return this.table.filter.type === 'duplicate_by_description'
                ? this.hasAnyPermission('duplicate-by-description-export')
                : this.hasAnyPermission('duplicate-by-part-number-export');
        },
    },

    mounted() {
        if (this.hasAnyPermission('duplicate-view')) {
            this.get();
        }
    },

    methods: {
        hasAnyPermission(...perms) {
            return perms.some((p) => this.authUserPermissions.includes(p));
        },
        confirmGenerate() {
            this.confirmDialog("Are you sure you want to generate duplicate data?", "generate it").then((result) => {
                if (result.isConfirmed) {
                    this.generateDuplicate();
                }
            });
        },

        // GET /report/check-duplicate (request-pool/catalouging-form/generate-duplicate)
        // -> { data: { description_total, manref_total } }
        async generateDuplicate() {
            this.generating = true;
            try {
                const response = await api(this, checkDuplicateService.generateDuplicate(), true);
                const data = response.data || {};
                this.summary.description_total = data.description_total || 0;
                this.summary.manref_total = data.manref_total || 0;
            } catch (error) {
                console.error("Generate duplicate error:", error);
                this.summary.description_total = 0;
                this.summary.manref_total = 0;
            } finally {
                this.generating = false;
            }
        },

        // duplicate_by_description & duplicate_by_manref -> tanpa body.
        buildBody() {
            return {};
        },

        // Normalisasi 1 record dari API ke field yang dipakai tabel.
        normalizeRow(item) {
            // duplicate_by_description: kategori langsung di item,
            // duplicates berupa JSON string
            if (this.table.filter.type === 'duplicate_by_description') {
                return {
                    ...item,
                    material_number: item.material_number,
                    short_description: item.po_text_horizontal || '',
                    long_description: item.po_text_horizontal || '',
                    material_type_description: item.material_type?.label || item.material_type?.description || '',
                    material_group_description: item.material_group?.label || item.material_group?.description || '',
                    uom_description: item.uom_data?.label || item.uom_data?.description || item.uom || '',
                    company_description: item.company?.label || item.company?.description || '',
                    duplicate_count: item.duplicate_count || 0,
                    duplicates: this.parseDuplicates(item.duplicates, item),
                };
            }

            // duplicate_by_manref: grouped by SPN + manufacture,
            // duplicates berupa array material lengkap
            // Parse duplicates dulu biar bisa dipake sebagai fallback data main row
            const manrefDups = this.parseDuplicates(item.duplicates, item);
            const firstDup = manrefDups[0] || {};
            return {
                ...item,
                // Pastikan id unik untuk data-key DataTable (pakai composite key SPN+manufacture)
                id: item.id || `manref-${item.spn || 'unknown'}-${item.manufacture || 'unknown'}`,
                material_number: item.spn || '',
                short_description: item.manufacture ? `Manufacturer: ${item.manufacture}` : '',
                long_description: '',
                // Fallback ke data duplikat pertama karena API gak return di root level
                material_type_description: item.material_type?.label || item.material_type?.description
                    || firstDup.material_type_description || '',
                material_group_description: item.material_group?.label || item.material_group?.description
                    || firstDup.material_group_description || '',
                uom_description: item.uom_data?.label || item.uom_data?.description || item.uom
                    || firstDup.uom_description || '',
                company_description: item.company?.label || item.company?.description
                    || firstDup.company_description || '',
                duplicate_count: item.duplicate_count || 0,
                duplicates: manrefDups,
            };
        },

        // duplicates bisa berupa JSON string (description) atau array (part_number).
        // Map ke field yang dipakai kartu duplikat.
        // parentItem digunakan sebagai fallback jika tiap item duplikat tidak punya data company/material dll.
        parseDuplicates(raw, parentItem = {}) {
            let arr = raw;
            if (typeof raw === 'string') {
                try {
                    arr = JSON.parse(raw);
                } catch (e) {
                    return [];
                }
            }
            if (!Array.isArray(arr)) return [];
            return arr.map(d => ({
                id: d.id,
                material_number: d.material_number,
                similarity: d.similarity, // undefined untuk part_number -> tampil "PART NUMBER MATCH"
                long_description: d.po_text_horizontal || '',
                material_type_description: d.material_type?.label || d.material_type?.description
                    || parentItem.material_type?.label || parentItem.material_type?.description || '',
                material_group_description: d.material_group?.label || d.material_group?.description
                    || parentItem.material_group?.label || parentItem.material_group?.description || '',
                uom_description: d.uom_data?.label || d.uom_data?.description || d.uom
                    || parentItem.uom_data?.label || parentItem.uom_data?.description || parentItem.uom || '',
                company_description: d.company?.label || d.company?.description
                    || parentItem.company?.label || parentItem.company?.description || '',
            }));
        },

        // append=false -> reset (page 1), append=true -> tambahkan page berikutnya (infinite scroll)
        async get(append = false) {
            if (append) {
                this.table.loadingMore = true;
            } else {
                this.table.onLoad = true;
                this.table.page.current_pages = 1;
            }
            try {
                const body = this.buildBody();
                const params = { page: this.table.page.current_pages };

                const response = await api(
                    this,
                    checkDuplicateService.list(this.table.filter.type, params, body),
                    false
                );

                const paged = response.data || {};
                const rows = Array.isArray(paged.data) ? paged.data : [];
                const normalized = rows.map(this.normalizeRow);

                if (append) {
                    this.table.data = [...this.table.data, ...normalized];
                } else {
                    this.table.data = normalized;
                }

                const rawLast = paged.last_updated || response.data?.last_updated || null;
                this.table.lastUpdated = rawLast ? moment(rawLast).format("DD MMM YYYY HH:mm") : null;

                this.table.total = paged.total || this.table.data.length;
                this.table.page.total_pages = paged.last_page || 1;
                this.table.page.current_pages = paged.current_page || this.table.page.current_pages;
                this.table.page.per_pages = paged.per_page || this.table.limit;
            } catch (error) {
                console.error("Fetch error:", error);
                if (!append) {
                    this.table.data = [];
                    this.table.total = 0;
                    this.table.lastUpdated = null;
                    this.table.page.total_pages = 1;
                }
            } finally {
                this.table.onLoad = false;
                this.table.loadingMore = false;
            }
        },

        // Infinite scroll: muat page berikutnya saat mendekati dasar container.
        onScroll(e) {
            const el = e.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
                this.loadMore();
            }
        },

        loadMore() {
            if (this.table.onLoad || this.table.loadingMore) return;
            if (this.table.page.current_pages >= this.table.page.total_pages) return;
            this.table.page.current_pages += 1;
            this.get(true);
        },

        syncUrl() {
            this.$router.push({
                path: this.$route.path,
                query: {
                    ...(this.table.search ? { search: this.table.search } : {}),
                    ...(this.table.filter.type ? { type: this.table.filter.type } : {}),
                },
            }).catch(() => { });
        },

        syncUrlAndGet() {
            this.syncUrl();
            this.table.page.current_pages = 1;
            this.get();
        },

        // Filter langsung dari dropdown di header
        onFilterChange() {
            this.table.search = "";
            this.expandedRows = {}; // reset expanded biar gak bentrok antar filter
            this.syncUrlAndGet();
        },

        getRowClass(data) {
            const count = data.duplicate_count || (data.duplicates ? data.duplicates.length : 0);
            return count > 0 ? 'row-has-duplicate' : '';
        },

        getSimilarityHexColor(similarity) {
            const val = similarity * 100;
            if (val >= 90) return '#2dce89'; // Success Green
            if (val >= 75) return '#fb6340'; // Warning Orange
            return '#adb5bd'; // Gray
        },

        extractShortText(text) {
            if (!text) return 'No Description';
            const firstLine = text.split('\n')[0].replace('===', '').replace('===', '').trim();
            return firstLine.length > 50 ? firstLine.substring(0, 47) + '...' : firstLine;
        },

        exportData() {
            if (this.table.onLoad) return;
            this.exportModal.selectedType = this.table.filter.type;
            this.exportModal.visible = true;
        },

        closeExportModal() {
            this.exportModal.visible = false;
            this.exportModal.selectedType = '';
            this.exportModal.onLoading = false;
        },

        async confirmExport() {
            if (!this.exportModal.selectedType) {
                this.$toastNotifPrime('warn', 'Warning', 'Please select a type!');
                return;
            }
            this.exportModal.onLoading = true;
            try {
                // Langsung panggil service, jangan pake api() helper karena blob response
                const response = await checkDuplicateService.exportDuplicate(this.exportModal.selectedType);

                // Tentukan filename dari header atau buat manual
                let filename;
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
                if (!filename) {
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                    filename = `duplicate_${this.exportModal.selectedType}_${timestamp}.xlsx`;
                }

                // Trigger download
                const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                this.exportModal.visible = false;
                this.$toastNotifPrime('success', 'Export Success', 'File download started.');
            } catch (error) {
                console.error("Export error:", error);
                const msg = error.response?.data?.message || 'Something went wrong.';
                this.$toastNotifPrime('error', 'Export Failed', msg);
            } finally {
                this.exportModal.onLoading = false;
            }
        },

        formatDate(date, mode = 'full') {
            if (!date) return '-';
            return mode === 'time' ? moment(date).format("HH:mm") : moment(date).format("DD MMM YYYY");
        },
    },
};
</script>

<style scoped>
.row-has-duplicate {
    background-color: #fff8f0 !important;
}

.duplicate-card-premium {
    transition: all 0.3s ease;
    border-left: 4px solid #dee2e6 !important;
}

.duplicate-card-premium:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important;
    border-left-color: #5e72e4 !important;
}

.lh-base {
    line-height: 1.5 !important;
}

.bg-danger-soft {
    background-color: rgba(245, 54, 92, 0.1) !important;
}

.bg-primary-soft {
    background-color: rgba(94, 114, 228, 0.1) !important;
}

.bg-info-soft {
    background-color: rgba(17, 205, 239, 0.1) !important;
}

.bg-gray-50 {
    background-color: #f8f9fa !important;
}

.w-60px {
    width: 60px !important;
}

.filter-select {
    min-width: 14rem;
}

/* Header tabel tetap menempel di atas saat infinite scroll */
:deep(.p-datatable-thead > tr > th) {
    position: sticky;
    top: 0;
    z-index: 1;
}
</style>
